
import { computed, defineComponent, ref, watch } from "vue";
import type { PropType } from "vue";

type FormMode = "create" | "edit" | "view";
export interface WithdrawalFormDestination {
  id?: string;
  address: string;
  amountBtc?: number;
  amountUsd?: number;
  note?: string;
  currency: "USD" | "BTC";
}

export interface WithdrawalFormValue {
  concept: string;
  note?: string;
  destinations: WithdrawalFormDestination[];
}

export interface WithdrawalFormSubmitPayload extends WithdrawalFormValue {
  totalBtc: number;
  totalUsd: number;
}

interface RecipientFormRow {
  id: string;
  address: string;
  amountBtc: string;
  amountUsd: string;
  note: string;
  currency: "USD" | "BTC";
  addressValid?: boolean;
  addressChecking?: boolean;
}

const buildRowId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const buildEmptyRecipient = (): RecipientFormRow => ({
  id: buildRowId(),
  address: "",
  amountBtc: "",
  amountUsd: "",
  note: "",
  currency: "USD",
  addressValid: undefined,
  addressChecking: false,
});

const toNumber = (value: string | number | undefined | null): number => {
  const num =
    typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(num) ? num : 0;
};

const sanitizeDecimal = (raw: string): string =>
  raw.replace(/,/g, ".").replace(/[^0-9.]/g, "");

export default defineComponent({
  name: "withdrawal-form",
  props: {
    mode: {
      type: String as PropType<FormMode>,
      default: "create",
    },
    value: {
      type: Object as PropType<WithdrawalFormValue | null>,
      default: null,
    },
    btcPrice: {
      type: Number,
      default: 0,
    },
    availableBtc: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      default: "Guardar",
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    allowAddRemove: {
      type: Boolean,
      default: true,
    },
    autoValidateOnLoad: {
      type: Boolean,
      default: false,
    },
    validateAddressFn: {
      type: Function as PropType<(address: string) => Promise<boolean>>, // optional async validator
      required: false,
    },
  },
  emits: ["submit", "validate-address", "change"],
  setup(props, { emit }) {
    const recipients = ref<Array<RecipientFormRow>>([buildEmptyRecipient()]);
    const concept = ref("");
    const transactionNote = ref("");

    const isEditable = computed(() => props.mode !== "view" && !props.readOnly);
    const allowAdd = computed(() => props.allowAddRemove === true);
    const allowRemove = computed(
      () => props.allowAddRemove === true && recipients.value.length > 1
    );

    const mapValueToRows = (payload?: WithdrawalFormValue | null) => {
      const dests = payload?.destinations || [];
      if (!dests.length) {
        recipients.value = [buildEmptyRecipient()];
        return;
      }
      recipients.value = dests.map((dest) => ({
        id: dest.id || buildRowId(),
        address: dest.address || "",
        amountBtc: dest.amountBtc !== undefined ? String(dest.amountBtc) : "",
        amountUsd: dest.amountUsd !== undefined ? String(dest.amountUsd) : "",
        note: dest.note || "",
        currency: dest.currency || "USD",
        addressValid: props.autoValidateOnLoad ? undefined : true,
        addressChecking: false,
      }));
    };

    const totalBtc = computed(() =>
      recipients.value.reduce((sum, recipient) => sum + rowBtc(recipient), 0)
    );

    const totalUsd = computed(() =>
      recipients.value.reduce((sum, recipient) => sum + rowUsd(recipient), 0)
    );

    const isBalanceExceeded = computed(
      () =>
        totalBtc.value > 0 &&
        props.availableBtc > 0 &&
        totalBtc.value > props.availableBtc
    );

    const duplicateAddresses = computed(() => {
      const seen = new Set<string>();
      const duplicates = new Set<string>();
      for (const r of recipients.value) {
        const addr = r.address.trim();
        if (!addr) continue;
        if (seen.has(addr)) {
          duplicates.add(addr);
        } else {
          seen.add(addr);
        }
      }
      return duplicates;
    });

    const validRecipients = computed(() =>
      recipients.value.filter((recipient) => rowIsComplete(recipient))
    );

    const allRowsValid = computed(() =>
      recipients.value.every(
        (recipient) => rowIsComplete(recipient) || rowIsEmpty(recipient)
      )
    );

    const allAddressesValid = computed(() =>
      recipients.value.every((recipient) => {
        if (!rowHasAddress(recipient)) return true;
        return recipient.addressValid === true || !props.validateAddressFn;
      })
    );

    const canSubmit = computed(() => {
      if (!isEditable.value) return false;
      return (
        validRecipients.value.length > 0 &&
        allRowsValid.value &&
        allAddressesValid.value &&
        !duplicateAddresses.value.size &&
        !props.loading &&
        !!concept.value.trim()
      );
    });

    const onBtcChange = (recipient: RecipientFormRow) => {
      if (recipient.currency !== "BTC") return;
      if (!props.btcPrice) {
        recipient.amountUsd = "";
        emitChange();
        return;
      }
      const btcRaw = String(recipient.amountBtc ?? "");
      recipient.amountBtc = sanitizeDecimal(btcRaw);
      const btc = toNumber(recipient.amountBtc);
      recipient.amountUsd = btc > 0 ? (btc * props.btcPrice).toFixed(2) : "";
      emitChange();
    };

    const onUsdChange = (recipient: RecipientFormRow) => {
      if (recipient.currency !== "USD") return;
      if (!props.btcPrice) {
        recipient.amountBtc = "";
        emitChange();
        return;
      }
      const usdRaw = String(recipient.amountUsd ?? "");
      recipient.amountUsd = sanitizeDecimal(usdRaw);
      const usd = toNumber(recipient.amountUsd);
      recipient.amountBtc = usd > 0 ? (usd / props.btcPrice).toFixed(8) : "";
      emitChange();
    };

    const blockNonNumeric = (event: KeyboardEvent) => {
      const allowedKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
      ];
      if (allowedKeys.includes(event.key)) return;
      if (!/[0-9.,]/.test(event.key)) {
        event.preventDefault();
        return;
      }
      const target = event.target as HTMLInputElement;
      if ([".", ","].includes(event.key)) {
        const hasDecimal =
          target.value.includes(".") || target.value.includes(",");
        if (hasDecimal) {
          event.preventDefault();
        }
      }
    };

    const addRecipient = () => {
      recipients.value.push(buildEmptyRecipient());
      emitChange();
    };

    const removeRecipient = (id: string) => {
      if (recipients.value.length === 1) {
        recipients.value = [buildEmptyRecipient()];
      } else {
        recipients.value = recipients.value.filter(
          (recipient) => recipient.id !== id
        );
      }
      emitChange();
    };

    const onCurrencyChange = (recipient: RecipientFormRow) => {
      if (!props.btcPrice) {
        emitChange();
        return;
      }
      if (recipient.currency === "USD") {
        const usd = toNumber(recipient.amountUsd);
        if (usd <= 0 && recipient.amountBtc) {
          const btc = toNumber(recipient.amountBtc);
          recipient.amountUsd =
            btc > 0 ? (btc * props.btcPrice).toFixed(2) : "";
        }
      } else {
        const btc = toNumber(recipient.amountBtc);
        if (btc <= 0 && recipient.amountUsd) {
          const usd = toNumber(recipient.amountUsd);
          recipient.amountBtc =
            usd > 0 ? (usd / props.btcPrice).toFixed(8) : "";
        }
      }
      emitChange();
    };

    const rowBtc = (recipient: RecipientFormRow): number => {
      if (recipient.currency === "BTC") return toNumber(recipient.amountBtc);
      const usd = toNumber(recipient.amountUsd);
      return props.btcPrice ? usd / props.btcPrice : 0;
    };

    const rowUsd = (recipient: RecipientFormRow): number => {
      if (recipient.currency === "USD") return toNumber(recipient.amountUsd);
      const btc = toNumber(recipient.amountBtc);
      return props.btcPrice ? btc * props.btcPrice : 0;
    };

    const rowHasAddress = (recipient: RecipientFormRow): boolean =>
      recipient.address.trim().length > 0;

    const addressIsDuplicate = (recipient: RecipientFormRow): boolean =>
      duplicateAddresses.value.has(recipient.address.trim());

    const addressInputClass = (recipient: RecipientFormRow) => ({
      "form-control": true,
      "form-control-sm": true,
      "is-invalid":
        addressIsDuplicate(recipient) || recipient.addressValid === false,
    });

    const rowHasAmount = (recipient: RecipientFormRow): boolean =>
      recipient.currency === "BTC"
        ? toNumber(recipient.amountBtc) > 0
        : toNumber(recipient.amountUsd) > 0;

    const rowIsComplete = (recipient: RecipientFormRow): boolean =>
      rowHasAddress(recipient) && rowHasAmount(recipient);

    const rowIsEmpty = (recipient: RecipientFormRow): boolean =>
      !rowHasAddress(recipient) && !rowHasAmount(recipient);

    const resetAddressValidation = (recipient: RecipientFormRow) => {
      recipient.addressValid = undefined;
      recipient.addressChecking = false;
    };

    const errorMessages = computed(() => {
      const messages: string[] = [];
      if (duplicateAddresses.value.size) {
        messages.push("No puede haber direcciones destino repetidas.");
      }
      const invalidAddresses = recipients.value.filter(
        (recipient) =>
          rowHasAddress(recipient) && recipient.addressValid === false
      );
      if (invalidAddresses.length > 0) {
        messages.push(
          "Hay direcciones inválidas. Verifica las entradas marcadas en rojo."
        );
      }
      return messages;
    });

    const warningMessages = computed(() => {
      const messages: string[] = [];
      if (isBalanceExceeded.value) {
        messages.push("El monto total excede el disponible.");
      }
      return messages;
    });

    const onAddressInput = (recipient: RecipientFormRow) => {
      resetAddressValidation(recipient);
      emitChange();
    };

    const validateAddress = async (recipient: RecipientFormRow) => {
      const addr = recipient.address.trim();
      if (!addr) {
        resetAddressValidation(recipient);
        return;
      }
      if (!props.validateAddressFn) {
        recipient.addressValid = true;
        emit("validate-address", addr);
        return;
      }
      recipient.addressChecking = true;
      recipient.addressValid = undefined;
      try {
        const validFlag = await props.validateAddressFn(addr);
        recipient.addressValid = Boolean(validFlag);
        emit("validate-address", addr);
      } catch (error) {
        console.error("Error validating address", error);
        recipient.addressValid = false;
      } finally {
        recipient.addressChecking = false;
        emitChange();
      }
    };

    const validateAllAddresses = async () => {
      if (!props.autoValidateOnLoad || !props.validateAddressFn) return;
      for (const recipient of recipients.value) {
        if (recipient.address.trim()) {
          await validateAddress(recipient);
        }
      }
    };

    const formatUsdDisplay = (value: number | string) => {
      const num = toNumber(value);
      return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    const formatBtcDisplay = (value: number | string) => {
      const num = toNumber(value);
      return num.toFixed(8);
    };

    const payload = computed<WithdrawalFormSubmitPayload>(() => ({
      concept: concept.value.trim(),
      note: transactionNote.value.trim() || undefined,
      destinations: validRecipients.value.map((recipient) => ({
        address: recipient.address.trim(),
        currency: recipient.currency,
        amountBtc:
          recipient.currency === "BTC"
            ? toNumber(recipient.amountBtc)
            : undefined,
        amountUsd:
          recipient.currency === "USD"
            ? toNumber(recipient.amountUsd)
            : undefined,
        note: recipient.note?.trim() || undefined,
      })),
      totalBtc: totalBtc.value,
      totalUsd: totalUsd.value,
    }));

    const emitChange = () => {
      emit("change", payload.value);
    };

    const emitSubmit = () => {
      if (!canSubmit.value) return;
      emit("submit", payload.value);
    };

    watch(
      () => props.value,
      (val) => {
        concept.value = val?.concept || "";
        transactionNote.value = val?.note || "";
        mapValueToRows(val);
        emitChange();
        validateAllAddresses();
      },
      { immediate: true, deep: true }
    );

    const showSubmit = computed(() => props.mode !== "view");
    const loadingState = computed(() => props.loading);
    const availableBtcValue = computed(() => props.availableBtc);
    const submitLabelText = computed(() => props.submitLabel);

    watch(
      () => props.btcPrice,
      () => {
        recipients.value.forEach((recipient) => {
          if (recipient.currency === "BTC" && recipient.amountBtc) {
            onBtcChange(recipient);
          }
          if (recipient.currency === "USD" && recipient.amountUsd) {
            onUsdChange(recipient);
          }
        });
      }
    );

    return {
      recipients,
      concept,
      transactionNote,
      isEditable,
      allowAdd,
      allowRemove,
      totalBtc,
      totalUsd,
      isBalanceExceeded,
      validRecipients,
      canSubmit,
      onBtcChange,
      onUsdChange,
      blockNonNumeric,
      validateAddress,
      resetAddressValidation,
      addRecipient,
      removeRecipient,
      onCurrencyChange,
      rowBtc,
      rowUsd,
      addressIsDuplicate,
      errorMessages,
      warningMessages,
      addressInputClass,
      formatUsdDisplay,
      formatBtcDisplay,
      emitSubmit,
      onAddressInput,
      showSubmit,
      loadingState,
      availableBtcValue,
      submitLabelText,
    };
  },
});
